// Executive Office proposal card.
// Answers exactly one question: "Do you want me to do this?"
// Outcome-first. Technical details live behind Show Details.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { friendlyError } from "@/lib/friendlyError";

/**
 * Translate a raw backend error message into a plain-English cause sentence.
 * The edge function already returns human-written strings for the known
 * proposal failure modes (stale target, permission, matched 0 records,
 * already up to date). This just tidies unknown cases so the toast never
 * shows "Try again in a moment" when we actually know what went wrong.
 */
function interpretProposalError(raw: string): string {
  const msg = (raw || "").trim();
  if (!msg) return "The backend didn't return a reason.";
  if (/no longer exists|already gone/i.test(msg)) return msg;
  if (/stale|rebuild the proposal|matched 0 records/i.test(msg)) return msg;
  if (/permission|not allowed|forbidden|row-level security/i.test(msg))
    return "You don't have permission to make this change while signed in as this user.";
  if (/expired/i.test(msg)) return "That proposal became outdated while other changes were made. Ask me again and I'll rebuild it from the current project.";
  if (/already applied/i.test(msg)) return "This proposal was already approved once — nothing more to do.";
  if (/not found/i.test(msg)) return msg;
  if (/Failed to send a request|network|fetch/i.test(msg))
    return "The request never reached the backend — check your connection and try again.";
  // Fallback: use the raw message verbatim so the Director sees the actual cause.
  return msg;
}

import {
  interpretPayrollError,
  isPayrollProposal,
  type InterpretedPayrollError,
  type PayrollNextAction,
} from "@/lib/payrollError";
import {
  forgetPendingRecovery,
  rememberPendingRecovery,
} from "@/lib/pendingRecovery";
import {
  Check,
  X,
  Pencil,
  ExternalLink,
  Loader2,
  Undo2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Sparkles,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";


export interface Proposal {
  pending_id: string;
  target_kind: string;
  target_table: string;
  target_id: string;
  target_label: string;
  action_kind: string;
  changes: Record<string, { before: unknown; after: unknown }>;
  reason?: string;
  href?: string;
  workflow_steps?: { label: string; done?: boolean }[];
}

type Status =
  | { kind: "pending" }
  | { kind: "editing" }
  | { kind: "applying" }
  | { kind: "applied"; auditId?: string; appliedAt: number }
  | { kind: "cancelled" }
  | { kind: "undone" };

// ---------- Human translation layer ----------

const CATEGORY_MAP: Record<string, string> = {
  instructor: "Instructor",
  payroll: "Payroll",
  registration: "Registration",
  class: "Classes",
  classes: "Classes",
  family: "Family",
  interest: "Registration",
  knowledge: "Learning Resources",
  memory: "Office Procedures",
  ui: "Website Improvements",
  workflow: "New Workflow",
  configuration: "Office Settings",
  settings: "Office Settings",
  period: "Registration",
  proposal: "Instructor",
  email: "Family",
  vendor: "Marketing",
};

function humanCategory(p: Proposal): string {
  const key = (p.target_kind || p.target_table || "").toLowerCase();
  for (const k of Object.keys(CATEGORY_MAP)) {
    if (key.includes(k)) return CATEGORY_MAP[k];
  }
  return "Office";
}

function titleCase(s: string) {
  return s.replace(/[_-]+/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

function outcomeTitle(p: Proposal): string {
  const label = p.target_label || "this record";
  const action = (p.action_kind || "").toLowerCase();
  const kind = (p.target_kind || p.target_table || "").toLowerCase();

  if (action === "resend_email") return `Resend ${label}'s Confirmation Email`;
  if (kind.includes("payroll")) return `Record ${label}'s Payroll`;
  if (kind.includes("instructor") && action.includes("create"))
    return `Add ${label} As An Instructor`;
  if (kind.includes("instructor") && action.includes("assign"))
    return `Assign ${label} To A Class`;
  if (kind.includes("instructor") && action.includes("merge"))
    return `Merge Duplicate Records For ${label}`;
  if (kind.includes("period") && action.includes("create"))
    return `Create ${label}`;
  if (kind.includes("period")) return `Update ${label}`;
  if (kind.includes("registration") && action.includes("move"))
    return `Move ${label} To A Different Class`;
  if (kind.includes("class") && action.includes("schedule"))
    return `Update The Schedule For ${label}`;
  if (kind.includes("class")) return `Update ${label}`;
  if (kind.includes("interest") || kind.includes("recommendation"))
    return `Dismiss The Alert For ${label}`;
  if (kind.includes("knowledge") || kind.includes("memory"))
    return `Save A New Note About ${label}`;
  if (p.workflow_steps?.length) return `Complete ${label}`;
  return `Update ${label}`;
}

function whyItMatters(p: Proposal): string {
  if (p.reason && p.reason.trim().length > 0) return p.reason.trim();
  const action = (p.action_kind || "").toLowerCase();
  const kind = (p.target_kind || p.target_table || "").toLowerCase();
  if (action === "resend_email")
    return `${p.target_label} didn't receive the original confirmation.`;
  if (kind.includes("payroll"))
    return `${p.target_label}'s payment is ready to be recorded.`;
  if (kind.includes("instructor") && action.includes("assign"))
    return `${p.target_label} is fully onboarded and ready to teach.`;
  if (kind.includes("instructor") && action.includes("merge"))
    return `Two records for ${p.target_label} are causing duplicate entries.`;
  if (p.workflow_steps?.length)
    return `A few steps remain before ${p.target_label} is complete.`;
  return `A small update is needed to keep ${p.target_label} current.`;
}

function willHappen(p: Proposal): string[] {
  if (p.workflow_steps?.length) {
    const remaining = p.workflow_steps.filter((s) => !s.done);
    if (remaining.length) return remaining.map((s) => s.label);
  }
  const action = (p.action_kind || "").toLowerCase();
  const kind = (p.target_kind || p.target_table || "").toLowerCase();
  const label = p.target_label;

  if (action === "resend_email")
    return [
      `${label} will receive the confirmation email again.`,
      `The delivery will be logged so you can see it went out.`,
    ];
  if (kind.includes("payroll"))
    return [
      `${label}'s payroll will be recorded.`,
      `Their payment history will be updated.`,
      `Payroll for this period will be complete.`,
    ];
  if (kind.includes("instructor") && action.includes("assign"))
    return [
      `${label} will be listed as the instructor for this class.`,
      `The class page will show their name to families.`,
    ];
  if (kind.includes("instructor") && action.includes("merge"))
    return [
      `The duplicate record will be combined into one profile.`,
      `All history and assignments will be preserved on the surviving profile.`,
    ];
  if (kind.includes("registration") && action.includes("move"))
    return [
      `${label}'s registration will move to the new class.`,
      `Their payment record will follow with them.`,
    ];
  if (kind.includes("period") && action.includes("create"))
    return [
      `${label} will appear as a selectable period.`,
      `You'll be able to sort classes and registrations under it.`,
    ];

  // Generic fallback derived from field count.
  const count = Object.keys(p.changes || {}).length;
  return [
    count > 0
      ? `${count === 1 ? "One detail" : `${count} details`} on ${label} will be updated.`
      : `${label} will be updated.`,
    `The change will be saved to your records.`,
  ];
}

function willNotChange(p: Proposal): string[] {
  const kind = (p.target_kind || p.target_table || "").toLowerCase();
  const action = (p.action_kind || "").toLowerCase();

  if (action === "resend_email")
    return [
      `The original registration stays exactly the same.`,
      `No payment will be charged or refunded.`,
    ];
  if (kind.includes("payroll"))
    return [
      `Their recurring pay rate stays the same.`,
      `Nothing will be sent to the instructor.`,
      `Previous payroll history stays intact.`,
    ];
  if (kind.includes("instructor") && action.includes("assign"))
    return [
      `No emails will go out to families.`,
      `Existing enrollments stay exactly as they are.`,
    ];
  if (kind.includes("instructor") && action.includes("merge"))
    return [
      `No families or payments will be contacted.`,
      `Class assignments stay in place.`,
    ];
  if (kind.includes("registration"))
    return [
      `No refunds or new charges will happen.`,
      `The family won't be notified automatically.`,
    ];
  return [
    `Nothing will be sent to families or instructors.`,
    `Unrelated records stay exactly as they are.`,
  ];
}

function recommendation(p: Proposal): string {
  const action = (p.action_kind || "").toLowerCase();
  const kind = (p.target_kind || p.target_table || "").toLowerCase();
  const label = p.target_label;
  if (action === "resend_email")
    return `Everything looks correct to me. This will send ${label}'s confirmation again without creating a new registration or charging anything. I recommend resending.`;
  if (kind.includes("payroll"))
    return `Everything looks correct to me. This will record ${label}'s one-time payment without changing their normal pay rate or future payroll. I recommend recording this payment.`;
  if (kind.includes("instructor") && action.includes("assign"))
    return `Everything looks correct to me. ${label} is fully onboarded and this simply attaches them to the class — no families are notified. I recommend approving.`;
  if (kind.includes("instructor") && action.includes("merge"))
    return `Everything looks correct to me. Merging combines the two records into one clean profile and keeps all history. I recommend merging.`;
  if (kind.includes("registration") && action.includes("move"))
    return `Everything looks correct to me. ${label}'s registration and payment move together, and the family isn't automatically notified. I recommend approving.`;
  return `Everything looks correct to me. This is a small update to ${label} with no downstream effects. I recommend approving.`;
}

// ---------- Component ----------

export function ProposedChangeCard({
  proposal,
  functionName = "silver-sage-assistant",
}: {
  proposal: Proposal;
  functionName?: string;
}) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>({ kind: "pending" });
  const [showDetails, setShowDetails] = useState(false);
  const [payrollError, setPayrollError] = useState<InterpretedPayrollError | null>(null);
  const [showTech, setShowTech] = useState(false);
  const [overrides, setOverrides] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      Object.entries(proposal.changes).map(([f, v]) => [f, String(v.after ?? "")])
    )
  );

  const [undoLeft, setUndoLeft] = useState(0);

  const canUndo = proposal.action_kind !== "resend_email";

  useEffect(() => {
    if (status.kind !== "applied") return;
    const tick = () => {
      const remaining = Math.max(0, 5 * 60 * 1000 - (Date.now() - status.appliedAt));
      setUndoLeft(remaining);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [status]);

  const call = async (body: Record<string, unknown>) => {
    const { data, error } = await supabase.functions.invoke(functionName, { body });
    if (error) throw new Error(error.message);
    if (data?.error) throw new Error(data.error);
    return data;
  };

  const isPayroll = isPayrollProposal(
    proposal.action_kind,
    proposal.target_kind,
    proposal.target_table
  );

  const onApprove = async () => {
    setStatus({ kind: "applying" });
    setPayrollError(null);
    const started = Date.now();
    try {
      const usedOverrides =
        status.kind === "editing"
          ? Object.fromEntries(
              Object.entries(overrides).filter(
                ([f, v]) => String(proposal.changes[f].after ?? "") !== v
              )
            )
          : undefined;
      const result = await call({
        action: "apply",
        pendingId: proposal.pending_id,
        overrides: usedOverrides,
      });
      // Success — clear any stale pending-recovery reminder for this proposal.
      forgetPendingRecovery(proposal.pending_id);
      // "Already up to date" — server marked consumed without changing anything.
      if (result?.already_up_to_date) {
        // eslint-disable-next-line no-console
        console.info("[SilverSage] Proposal already reflected in database", {
          pending_id: proposal.pending_id,
          action_kind: proposal.action_kind,
          target_table: proposal.target_table,
          target_id: proposal.target_id,
          target_label: proposal.target_label,
          note: result.note,
        });
        toast.success("Already up to date.", {
          description: result.note ?? `${proposal.target_label} already matches this proposal — nothing needed to change.`,
        });
        setStatus({ kind: "applied", auditId: undefined, appliedAt: Date.now() });
        return;
      }
      setStatus({ kind: "applied", auditId: result?.audit_id, appliedAt: Date.now() });
    } catch (e) {
      setStatus({ kind: "pending" });
      // Rich diagnostic — one approval attempt is enough to see everything.
      // eslint-disable-next-line no-console
      console.error("[SilverSage] Proposal approval failed", {
        pending_id: proposal.pending_id,
        action_kind: proposal.action_kind,
        target_kind: proposal.target_kind,
        target_table: proposal.target_table,
        target_id: proposal.target_id,
        target_label: proposal.target_label,
        payload: {
          action: "apply",
          pendingId: proposal.pending_id,
          overrides:
            status.kind === "editing"
              ? Object.fromEntries(
                  Object.entries(overrides).filter(
                    ([f, v]) => String(proposal.changes[f].after ?? "") !== v
                  )
                )
              : undefined,
        },
        function: functionName,
        elapsed_ms: Date.now() - started,
        raw_error: e,
        raw_message: e instanceof Error ? e.message : String(e),
      }, e);
      if (isPayroll) {
        const instructorHref = proposal.target_id
          ? `/admin/instructors?instructor=${proposal.target_id}`
          : proposal.href;
        const interpreted = interpretPayrollError(e, proposal.target_label, {
          instructorHref,
        });
        setPayrollError(interpreted);
        setShowTech(false);

        // Transient failures: quietly remember so morning brief brings it back.
        if (interpreted.transient) {
          rememberPendingRecovery({
            id: proposal.pending_id,
            category: "payroll",
            subject: `${proposal.target_label}'s payroll`,
            what: interpreted.what,
            reason: "Payroll service temporarily unavailable",
            href: proposal.href || "/admin/payroll",
            reopenLabel: `Finish recording ${proposal.target_label}'s payment`,
            createdAt: Date.now(),
            technical: interpreted.technical,
          });
        }
      } else {
        // Surface the real cause. Server-side messages are already written for humans
        // (stale proposal, no longer exists, permission, matched 0 records, etc.);
        // fall back to a generic sentence only when we truly have nothing.
        const rawMsg = e instanceof Error ? e.message : String(e);
        const humanCause = interpretProposalError(rawMsg);
        friendlyError({
          what: `I couldn't finish "${title}".`,
          meaning: humanCause,
          changed: "Nothing has changed yet.",
          nextStep: humanCause.includes("no longer exists") || humanCause.includes("stale") || humanCause.includes("0 records")
            ? "Ask me again in the chat and I'll rebuild the proposal from the current record."
            : "Open Show Technical Details for the exact backend response.",
          technical: e,
        });
      }
    }
  };



  const onCancel = async () => {
    try {
      await call({ action: "cancel", pendingId: proposal.pending_id });
    } catch {
      /* silent — the card is being dismissed anyway */
    }
    setStatus({ kind: "cancelled" });
  };

  const onUndo = async () => {
    if (status.kind !== "applied" || !status.auditId) return;
    try {
      await call({ action: "undo", auditId: status.auditId });
      setStatus({ kind: "undone" });
    } catch (e) {
      friendlyError({
        what: "I couldn't undo that just now.",
        nextStep: "Try again, or make the change manually.",
        technical: e,
      });
    }
  };

  const title = useMemo(() => outcomeTitle(proposal), [proposal]);
  const why = useMemo(() => whyItMatters(proposal), [proposal]);
  const happens = useMemo(() => willHappen(proposal), [proposal]);
  const preserves = useMemo(() => willNotChange(proposal), [proposal]);
  const rec = useMemo(() => recommendation(proposal), [proposal]);
  const category = useMemo(() => humanCategory(proposal), [proposal]);
  const changeEntries = useMemo(() => Object.entries(proposal.changes), [proposal.changes]);

  // Terminal states
  if (status.kind === "cancelled") {
    return (
      <QuietBanner
        icon={<X className="h-4 w-4" />}
        title="Not now"
        detail={`Nothing was changed for ${proposal.target_label}.`}
      />
    );
  }
  if (status.kind === "undone") {
    return (
      <QuietBanner
        icon={<Undo2 className="h-4 w-4" />}
        title="Reversed"
        detail={`${proposal.target_label} is back to where it was.`}
      />
    );
  }
  if (status.kind === "applied") {
    return (
      <div className="rounded-lg border border-emerald-600/30 bg-emerald-50/50 p-4 space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-emerald-800">
          <CheckCircle2 className="h-4 w-4" />
          Done. {title}.
        </div>
        {canUndo && undoLeft > 0 && (
          <div className="flex items-center justify-between text-xs text-emerald-900/70">
            <span>You can reverse this for {Math.ceil(undoLeft / 1000)}s.</span>
            <Button size="sm" variant="ghost" onClick={onUndo}>
              <Undo2 className="h-3 w-3 mr-1" /> Reverse
            </Button>
          </div>
        )}
      </div>
    );
  }

  const editing = status.kind === "editing";
  const applying = status.kind === "applying";

  return (
    <div
      data-proposal-id={proposal.pending_id}
      data-proposal-kind={proposal.target_kind ?? proposal.action_kind ?? "update"}
      className="rounded-xl border border-primary/30 bg-card p-5 space-y-4 scroll-mt-24 shadow-sm"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1.5">
          <Badge variant="secondary" className="gap-1">
            <Sparkles className="h-3 w-3" /> {category}
          </Badge>
          <h3 className="text-lg font-serif font-semibold leading-tight">
            {title}
          </h3>
        </div>
        {proposal.href && (
          <Button asChild size="sm" variant="ghost">
            <Link to={proposal.href}>
              Open <ExternalLink className="h-3 w-3 ml-1" />
            </Link>
          </Button>
        )}
      </div>

      {/* Why */}
      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
          Why I'm recommending this
        </div>
        <p className="text-sm">{why}</p>
      </div>

      {/* What will happen */}
      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
          If you approve
        </div>
        <ul className="space-y-1">
          {happens.map((line, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <Check className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* What won't change */}
      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
          What will not change
        </div>
        <ul className="space-y-1">
          {preserves.map((line, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
              <Check className="h-4 w-4 mt-0.5 shrink-0 opacity-60" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Recommendation */}
      <div className="rounded-md bg-muted/50 border border-border/60 px-3 py-2 text-sm italic">
        {rec}
      </div>

      {/* Editing surface — only when Edit is clicked */}
      {editing && changeEntries.length > 0 && (
        <div className="space-y-2 rounded-md border border-dashed p-3">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            Adjust before approving
          </div>
          {changeEntries.map(([field, { after }]) => (
            <div key={field} className="grid grid-cols-[140px,1fr] gap-3 items-center">
              <Label className="text-xs capitalize text-muted-foreground">
                {field.replace(/_/g, " ")}
              </Label>
              <Input
                value={overrides[field] ?? String(after ?? "")}
                onChange={(e) =>
                  setOverrides((o) => ({ ...o, [field]: e.target.value }))
                }
                className="h-8"
              />
            </div>
          ))}
        </div>
      )}

      {/* Recovery panel — Chief-of-Staff voice. Ends on "here's what we can do next." */}
      {payrollError && (
        <div className="rounded-md border border-amber-500/40 bg-amber-50 dark:bg-amber-950/20 p-4 space-y-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-700 shrink-0" />
            <div className="space-y-2">
              <div className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                {payrollError.title}
              </div>
              <p className="text-sm text-amber-900/90 dark:text-amber-100/90 leading-relaxed">
                {payrollError.what}
              </p>
              <p className="text-sm text-amber-900/80 dark:text-amber-100/80 leading-relaxed">
                {payrollError.changed}
              </p>
              <p className="text-sm text-amber-900/80 dark:text-amber-100/80 leading-relaxed">
                {payrollError.reassurance}
              </p>
            </div>
          </div>

          <div className="pt-1 pl-6 space-y-2">
            <div className="text-xs uppercase tracking-wide text-amber-900/70 dark:text-amber-100/70">
              Here's what we can do next
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <RecoveryButton
                action={payrollError.nextAction}
                primary
                pending={applying}
                onNavigate={(href) => navigate(href)}
                onRetry={() => {
                  setPayrollError(null);
                  onApprove();
                }}
                onRefresh={() => window.location.reload()}
              />
              {payrollError.alternate && (
                <>
                  <span className="text-xs text-amber-900/60 dark:text-amber-100/60">or</span>
                  <RecoveryButton
                    action={payrollError.alternate}
                    pending={applying}
                    onNavigate={(href) => navigate(href)}
                    onRetry={() => {
                      setPayrollError(null);
                      onApprove();
                    }}
                    onRefresh={() => window.location.reload()}
                  />
                </>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={onCancel}
                disabled={applying}
                className="text-amber-900/70 dark:text-amber-100/70"
              >
                I'll Finish This Later
              </Button>
            </div>
            {payrollError.transient && (
              <p className="text-xs text-amber-900/70 dark:text-amber-100/70 italic">
                I'll remind you in tomorrow's morning brief if this is still waiting.
              </p>
            )}
          </div>

          <div className="pt-1 pl-6">
            <button
              type="button"
              onClick={() => setShowTech((v) => !v)}
              className="text-xs text-amber-900/70 dark:text-amber-100/70 hover:underline inline-flex items-center gap-1"
            >
              {showTech ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              Show Technical Details
            </button>
            {showTech && (
              <pre className="mt-2 text-[11px] whitespace-pre-wrap break-words rounded bg-background/70 border border-amber-500/30 p-2 text-muted-foreground">
                {payrollError.technical}
                {"\n"}—{"\n"}function: {functionName}
                {"\n"}action: {proposal.action_kind}
                {"\n"}pending_id: {proposal.pending_id}
              </pre>
            )}
          </div>
        </div>
      )}

      {/* Buttons — hidden while the recovery panel owns the actions. */}
      {!payrollError && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button size="sm" onClick={onApprove} disabled={applying} className="gap-1">
            {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Approve
          </Button>
          {proposal.action_kind !== "resend_email" && changeEntries.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setStatus(editing ? { kind: "pending" } : { kind: "editing" })}
              disabled={applying}
              className="gap-1"
            >
              <Pencil className="h-4 w-4" />
              {editing ? "Done editing" : "Edit"}
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={applying}>
            Continue Later
          </Button>
        </div>
      )}


      {/* Progressive disclosure */}
      {(changeEntries.length > 0 || proposal.workflow_steps?.length) && (
        <div className="pt-1 border-t border-border/60">
          <button
            type="button"
            onClick={() => setShowDetails((v) => !v)}
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            {showDetails ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            Show Details
          </button>
          {showDetails && (
            <div className="mt-2 space-y-2 text-xs text-muted-foreground">
              {proposal.workflow_steps?.length ? (
                <ul className="space-y-1">
                  {proposal.workflow_steps.map((s, i) => (
                    <li key={i}>
                      {s.done ? "✓" : "○"} {s.label}
                    </li>
                  ))}
                </ul>
              ) : null}
              {changeEntries.length > 0 && (
                <div className="space-y-1">
                  {changeEntries.map(([field, { before, after }]) => (
                    <div key={field} className="grid grid-cols-[140px,1fr] gap-2">
                      <span className="capitalize">{field.replace(/_/g, " ")}</span>
                      <span>
                        <span className="line-through opacity-60">
                          {before === null || before === "" ? "(blank)" : String(before)}
                        </span>
                        {" → "}
                        <span className="text-foreground">
                          {String(overrides[field] ?? after ?? "")}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <div className="opacity-70">
                Reference: {proposal.target_table} · {titleCase(proposal.action_kind || "update")}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function QuietBanner({
  icon,
  title,
  detail,
}: {
  icon: React.ReactNode;
  title: string;
  detail: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm">
      <div className="flex items-center gap-2 font-medium">
        {icon}
        {title}
      </div>
      <div className="text-xs text-muted-foreground mt-1">{detail}</div>
    </div>
  );
}

function RecoveryButton({
  action,
  primary,
  pending,
  onNavigate,
  onRetry,
  onRefresh,
}: {
  action: PayrollNextAction;
  primary?: boolean;
  pending?: boolean;
  onNavigate: (href: string) => void;
  onRetry: () => void;
  onRefresh: () => void;
}) {
  const handleClick = () => {
    if (action.kind === "refresh_session") return onRefresh();
    if (action.isRetry || action.kind === "try_again") return onRetry();
    if (action.href) return onNavigate(action.href);
  };
  return (
    <Button
      size="sm"
      variant={primary ? "default" : "outline"}
      onClick={handleClick}
      disabled={pending}
      className="gap-1"
    >
      {pending && (action.isRetry || action.kind === "try_again") ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <ArrowRight className="h-4 w-4" />
      )}
      {action.label}
    </Button>
  );
}
